import { useMemo, useState } from 'react';
import { Check, ChevronDown, UserPlus } from 'lucide-react';
import {
  useActiveCollaborators,
  useAssignTask,
  useUnassignTask,
} from '../../hooks/queries/useTaskCollaboration';
import type { CollaboratorSummary } from '../../services/tasks/taskCollaborationService';
import { Avatar } from '../ui/Avatar';

interface TaskAssigneeDropdownProps {
  taskId?: string | null;
  assignees: CollaboratorSummary[];
  onLocalToggle?: (collaborator: CollaboratorSummary) => void;
}

export function TaskAssigneeDropdown({
  taskId,
  assignees,
  onLocalToggle,
}: TaskAssigneeDropdownProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const { data: collaborators = [] } = useActiveCollaborators();
  const assignMut = useAssignTask();
  const unassignMut = useUnassignTask();

  const assigneeIds = useMemo(() => new Set(assignees.map((a) => a.id)), [assignees]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return collaborators;
    return collaborators.filter(
      (c) =>
        c.full_name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [collaborators, filter]);

  function toggle(c: CollaboratorSummary) {
    if (!taskId) {
      onLocalToggle?.(c);
      return;
    }
    if (assigneeIds.has(c.id)) {
      unassignMut.mutate({ taskId, assigneeId: c.id });
    } else {
      assignMut.mutate({ taskId, assigneeId: c.id });
    }
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-neutral-200 text-xs text-cime-text-muted hover:bg-neutral-50 hover:border-cime-primary/30"
      >
        <UserPlus className="w-3.5 h-3.5" />
        {assignees.length === 0 ? 'Assigner' : `${assignees.length} assigné${assignees.length > 1 ? 's' : ''}`}
        <ChevronDown className="w-3 h-3 opacity-50" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute z-20 mt-1 right-0 w-72 rounded-lg border border-neutral-200 bg-white shadow-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-neutral-100">
              <input
                autoFocus
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Rechercher un intervenant…"
                className="w-full text-sm bg-transparent border-0 outline-none"
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-xs text-cime-text-muted text-center">
                  Aucun intervenant trouvé.
                </div>
              )}
              {filtered.map((c) => {
                const checked = assigneeIds.has(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50"
                  >
                    <Avatar name={c.full_name} src={c.avatar_url} size="xs" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-cime-text-primary truncate">
                        {c.full_name}
                      </div>
                      <div className="text-[11px] text-cime-text-muted truncate">
                        {c.role}
                      </div>
                    </div>
                    {checked && (
                      <Check className="w-4 h-4 text-cime-primary flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
